/* sugar:assets/sugar-interactive.js@2.0.0 */
/*
 * Sugar interactive primitives — first-party replacement for Alpine + Collapse.
 *
 * Three engines, each driven by data-* attributes so Liquid stays declarative and
 * theme-editor-injected markup wires itself up with no re-init call:
 *
 *   accordion  [data-accordion-group] > [data-accordion] > trigger + panel
 *   tabs       [data-tabs] with a JSON config attribute
 *   quiz       window.SugarQuiz — a plain observable store + subscriber renders
 *
 * Everything is delegated from the document, so a block that arrives later
 * (section rendering API, editor block add/move) works without being told.
 * `<sugar-accordion>`, `<sugar-tabs>` and `<sugar-quiz>` are registered as thin
 * custom elements that just stamp the group attributes, for markup that can
 * afford its own wrapper tag.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  // WAAPI pauses in background tabs, so a promise/finish handler would never
  // settle there — state must never depend on an animation completing.
  function instant() {
    return REDUCED.matches || document.hidden;
  }

  function attr(el, name) {
    return el && el.getAttribute ? el.getAttribute(name) : null;
  }

  function parseJSON(raw, fallback) {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function emit(el, name, detail) {
    el.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  /* ================================================================== */
  /* Accordion                                                           */
  /* ================================================================== */

  var Accordion = {
    DEFAULT_DURATION: 300,

    group: function (row) {
      return row.closest('[data-accordion-group]');
    },

    panel: function (row) {
      return row.querySelector('[data-accordion-panel]');
    },

    trigger: function (row) {
      return row.querySelector('[data-accordion-trigger]');
    },

    duration: function (row) {
      var group = Accordion.group(row);
      var raw = attr(row, 'data-duration') || (group && attr(group, 'data-duration'));
      var n = Number(raw);
      return isFinite(n) && n >= 0 ? n : Accordion.DEFAULT_DURATION;
    },

    isOpen: function (row) {
      return row.classList.contains('is-open');
    },

    // Cancel any in-flight animation and report the panel's CURRENT rendered
    // height, so a re-click mid-animation reverses from where it visually is.
    interrupt: function (panel) {
      var current = panel.getBoundingClientRect().height;
      Accordion.settle(panel);
      return current;
    },

    // Dropping the reference is not enough: a WAAPI animation that has not
    // finished keeps applying its current keyframe height, and the animation
    // cascade outranks the inline style we clear here. It must be cancelled.
    settle: function (panel) {
      if (panel._sugarTimer) {
        clearTimeout(panel._sugarTimer);
        panel._sugarTimer = null;
      }
      if (panel._sugarAnim) {
        var anim = panel._sugarAnim;
        panel._sugarAnim = null;
        try {
          anim.cancel();
        } catch (e) {}
      }
      panel.style.overflow = '';
      panel.style.height = '';
    },

    // WAAPI stops advancing in a backgrounded tab, so `finish` may never fire
    // for an animation that started while the page was still visible. Every
    // animated open/close therefore arms a timer as well, and both paths run
    // through this idempotent finalizer.
    animate: function (panel, from, to, duration, done) {
      panel.style.overflow = 'hidden';

      var settled = false;
      var finalize = function () {
        if (settled) return;
        settled = true;
        Accordion.settle(panel);
        if (done) done();
      };

      var anim = panel.animate({ height: [from + 'px', to + 'px'] }, { duration: duration, easing: 'ease' });
      panel._sugarAnim = anim;
      anim.addEventListener('finish', finalize);
      anim.addEventListener('cancel', function () {
        settled = true;
      });
      panel._sugarTimer = setTimeout(finalize, duration + 60);
    },

    // A closed panel is `hidden`, so anything inside it that sizes itself when
    // it starts up — a slider, a rail drawn between measured points, a track
    // whose width is read in JS — came up against a zero-width box and stayed
    // that way. The first time a panel is shown, hand its contents a chance to
    // measure again: Splide re-reads the track itself, and anything else picks
    // up `sugar:visible`.
    reveal: function (panel) {
      if (panel._sugarRevealed) return;
      panel._sugarRevealed = true;
      // A timer, not rAF: a backgrounded tab never paints, and a panel opened
      // there would keep the zero-width measurements it is being handed a
      // chance to correct.
      setTimeout(function () {
        var sliders = panel.querySelectorAll('.splide');
        for (var i = 0; i < sliders.length; i++) {
          var inst = sliders[i]._splideInstance;
          if (!inst) continue;
          try {
            inst.refresh();
          } catch (e) {}
        }
        emit(panel, 'sugar:visible', {});
      });
    },

    open: function (row, animate) {
      var panel = Accordion.panel(row);
      if (!panel) return;

      var group = Accordion.group(row);
      if (group && group.hasAttribute('data-single')) {
        var siblings = group.querySelectorAll('[data-accordion].is-open');
        for (var i = 0; i < siblings.length; i++) {
          if (siblings[i] !== row && Accordion.group(siblings[i]) === group) Accordion.close(siblings[i], animate);
        }
      }

      // A hidden panel has no height to reverse from, but it may still carry a
      // stalled animation from an earlier close — settle before re-opening.
      var from = 0;
      if (panel.hidden) Accordion.settle(panel);
      else from = Accordion.interrupt(panel);
      panel.hidden = false;
      row.classList.add('is-open');
      Accordion.reveal(panel);
      var trigger = Accordion.trigger(row);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      if (animate === false || instant()) {
        Accordion.settle(panel);
        emit(row, 'sugar:accordion:toggle', { open: true });
        return;
      }

      Accordion.animate(panel, from, panel.scrollHeight, Accordion.duration(row));
      emit(row, 'sugar:accordion:toggle', { open: true });
    },

    close: function (row, animate) {
      var panel = Accordion.panel(row);
      if (!panel) return;

      row.classList.remove('is-open');
      var trigger = Accordion.trigger(row);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      if (animate === false || instant()) {
        Accordion.settle(panel);
        panel.hidden = true;
        emit(row, 'sugar:accordion:toggle', { open: false });
        return;
      }

      var from = Accordion.interrupt(panel);
      Accordion.animate(panel, from, 0, Accordion.duration(row), function () {
        panel.hidden = true;
      });
      emit(row, 'sugar:accordion:toggle', { open: false });
    },

    toggle: function (row) {
      Accordion.isOpen(row) ? Accordion.close(row) : Accordion.open(row);
    },

    // Rows render closed-and-hidden from Liquid; this promotes the ones that
    // should start open (per-row `data-open`, or the group's `data-open-first`
    // / `data-open-all`).
    init: function (scope) {
      var root = scope && scope.querySelectorAll ? scope : document;
      var rows = root.querySelectorAll('[data-accordion]');
      var all = [];
      if (root.matches && root.matches('[data-accordion]')) all.push(root);
      for (var i = 0; i < rows.length; i++) all.push(rows[i]);

      for (var j = 0; j < all.length; j++) {
        var row = all[j];
        if (row._sugarAccordionReady) continue;
        row._sugarAccordionReady = true;

        var group = Accordion.group(row);
        var openAll = !!(group && group.hasAttribute('data-open-all'));
        var openFirst = !!(group && group.hasAttribute('data-open-first') && Accordion.firstInGroup(group) === row);

        if (openAll || openFirst || row.hasAttribute('data-open')) {
          Accordion.open(row, false);
        } else {
          var panel = Accordion.panel(row);
          if (panel) panel.hidden = true;
          var trigger = Accordion.trigger(row);
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }
      }
    },

    firstInGroup: function (group) {
      var rows = group.querySelectorAll('[data-accordion]');
      for (var i = 0; i < rows.length; i++) {
        if (Accordion.group(rows[i]) === group) return rows[i];
      }
      return null;
    },

    // Used by the FAQ section's expand-all / collapse-all controls.
    setAll: function (group, open) {
      var rows = group.querySelectorAll('[data-accordion]');
      for (var i = 0; i < rows.length; i++) {
        open ? Accordion.open(rows[i]) : Accordion.close(rows[i]);
      }
    },

    allOpen: function (group) {
      var rows = group.querySelectorAll('[data-accordion]');
      if (!rows.length) return false;
      for (var i = 0; i < rows.length; i++) if (!Accordion.isOpen(rows[i])) return false;
      return true;
    },

    allClosed: function (group) {
      var rows = group.querySelectorAll('[data-accordion]');
      for (var i = 0; i < rows.length; i++) if (Accordion.isOpen(rows[i])) return false;
      return true;
    }
  };

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    var row = trigger.closest('[data-accordion]');
    if (!row) return;
    e.preventDefault();
    Accordion.toggle(row);
  });

  /* ================================================================== */
  /* Template stamping                                                   */
  /* ================================================================== */

  /*
   * Some list blocks let every row share one icon, so Liquid renders that icon
   * once into a <template> and leaves a placeholder in each row. Stamping is a
   * single pass with no state — it just needs to happen after parse, and again
   * for markup the editor injects later.
   *
   * When the shared icon is a number, each copy shows the next one up. The digit
   * is rewritten per copy rather than left to a CSS counter, because the filled
   * shapes cut their digit out of an SVG mask, where generated content cannot
   * reach — and because only the real number knows whether it needs the
   * two-digit size.
   */
  function stampNumber(frag, value, uid) {
    var icon = frag.querySelector('.sugar-icon-number');
    if (!icon) return;
    var text = String(value);
    icon.classList.toggle('sugar-icon-number--two', text.length > 1);

    var svgText = icon.querySelector('text');
    if (!svgText) {
      var span = icon.querySelector('span');
      if (span) span.textContent = text;
      return;
    }
    svgText.textContent = text;

    // Copies share the template's mask id, so each one would paint the first
    // copy's digit until its own mask is given a unique name.
    var mask = icon.querySelector('mask');
    if (!mask) return;
    var id = (mask.getAttribute('id') || 'sic') + '-' + uid;
    mask.setAttribute('id', id);
    var painted = icon.querySelector('[mask]');
    if (painted) painted.setAttribute('mask', 'url(#' + id + ')');
  }

  var stampSeq = 0;

  function stamp(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var hosts = [];
    if (root.matches && root.matches('[data-stamp]')) hosts.push(root);
    var found = root.querySelectorAll('[data-stamp]');
    for (var i = 0; i < found.length; i++) hosts.push(found[i]);

    for (var j = 0; j < hosts.length; j++) {
      var host = hosts[j];
      var template = host.querySelector('[data-stamp-template]');
      if (!template || !template.content) continue;

      var start = parseInt(host.getAttribute('data-stamp-number'), 10);
      var counting = !isNaN(start);

      var slots = host.querySelectorAll('[data-stamp-slot]');
      for (var k = 0; k < slots.length; k++) {
        var copy = template.content.cloneNode(true);
        if (counting) stampNumber(copy, start + k, stampSeq++);
        slots[k].replaceWith(copy);
      }
    }
  }

  /* ================================================================== */
  /* Clamp ("read more")                                                 */
  /* ================================================================== */

  /*
   * Long copy is clamped to a CSS-driven max height, and the toggle only appears
   * when the text actually overflows it. That can only be known after layout, so
   * it is measured rather than decided in Liquid, and re-measured when the
   * column width changes and the text reflows.
   */
  var Clamp = {
    init: function (scope) {
      var root = scope && scope.querySelectorAll ? scope : document;
      var hosts = [];
      if (root.matches && root.matches('[data-clamp]')) hosts.push(root);
      var found = root.querySelectorAll('[data-clamp]');
      for (var i = 0; i < found.length; i++) hosts.push(found[i]);

      for (var j = 0; j < hosts.length; j++) {
        if (hosts[j]._sugarClamp) continue;
        hosts[j]._sugarClamp = true;
        Clamp.setup(hosts[j]);
      }
    },

    setup: function (host) {
      var cfg = parseJSON(attr(host, 'data-clamp'), {}) || {};
      var content = host.querySelector('[data-clamp-content]');
      var toggle = host.querySelector('[data-clamp-toggle]');
      if (!content) return;

      var expanded = false;

      function limit() {
        var raw = getComputedStyle(content).getPropertyValue(cfg.var || '');
        return parseInt(raw, 10);
      }

      function render() {
        var max = limit();
        var overflows = isFinite(max) && max > 0 && content.scrollHeight > max;
        var clamped = overflows && !expanded;

        content.classList.toggle('is-clamped', clamped);
        content.classList.toggle('has-gradient', clamped && !!cfg.gradient);

        if (toggle) {
          toggle.hidden = !overflows;
          toggle.innerHTML = expanded ? cfg.less || '' : cfg.more || '';
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        }
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          expanded = !expanded;
          render();
          emit(host, 'sugar:clamp:toggle', { expanded: expanded });
        });
      }

      // scrollHeight is only meaningful once the clamp is off, so measure with
      // it lifted and restore whatever the first render decides.
      function measure() {
        content.classList.remove('is-clamped', 'has-gradient');
        render();
      }

      measure();
      requestAnimationFrame(measure);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure).catch(function () {});
      }

      var timer;
      window.addEventListener('resize', function () {
        clearTimeout(timer);
        timer = setTimeout(measure, 150);
      });
    }
  };

  /* ================================================================== */
  /* Rotator                                                             */
  /* ================================================================== */

  /*
   * Cycles `.is-active` through a list of items on a timer, holding the
   * outgoing item as `.is-exiting` for the length of the CSS transition so the
   * two can cross-fade. Only the visible item is exposed to assistive tech, so the
   * cycle is not read out as one long run-on line. No bindings — class toggling.
   *
   * `bounce` walks the list forward and back (1 2 3 2 1 2 …) instead of wrapping,
   * and marks every item before the active one `.is-before`, so CSS can park those
   * above and the rest below: moving forward slides up, moving back slides down.
   */
  var Rotator = {
    ITEM: '.rotating-text-item',

    init: function (scope) {
      var root = scope && scope.querySelectorAll ? scope : document;
      var hosts = [];
      if (root.matches && root.matches('[data-rotate]')) hosts.push(root);
      var found = root.querySelectorAll('[data-rotate]');
      for (var i = 0; i < found.length; i++) hosts.push(found[i]);

      for (var j = 0; j < hosts.length; j++) {
        if (hosts[j]._sugarRotator) continue;
        hosts[j]._sugarRotator = true;
        Rotator.setup(hosts[j]);
      }
    },

    setup: function (host) {
      var cfg = parseJSON(attr(host, 'data-rotate'), {}) || {};
      var wrap = host.querySelector('[data-rotate-items]');
      if (!wrap) return;

      var speed = Number(cfg.speed) || 400;
      var pause = Number(cfg.pause) || 3000;
      var bounce = !!cfg.bounce;
      var items = [];
      var index = 0;
      var step = 1;
      var timer = null;

      function markBefore() {
        if (!bounce) return;
        for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-before', i < index);
      }

      function stop() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      function start() {
        stop();
        if (items.length > 1) timer = setInterval(next, pause);
      }

      function next() {
        if (items.length < 2) return;
        var current = items[index];
        current.classList.add('is-exiting');
        current.classList.remove('is-active');
        current.setAttribute('aria-hidden', 'true');
        if (bounce) {
          if (index + step < 0 || index + step >= items.length) step = -step;
          index += step;
        } else {
          index = (index + 1) % items.length;
        }
        markBefore();
        items[index].classList.add('is-active');
        items[index].removeAttribute('aria-hidden');
        setTimeout(function () {
          current.classList.remove('is-exiting');
        }, speed);
      }

      function refresh() {
        stop();
        items = [].slice.call(wrap.querySelectorAll(cfg.item || Rotator.ITEM));
        index = 0;
        step = 1;
        items.forEach(function (item) {
          item.classList.remove('is-active', 'is-exiting', 'is-before');
          item.setAttribute('aria-hidden', 'true');
        });
        if (items.length) {
          items[0].classList.add('is-active');
          items[0].removeAttribute('aria-hidden');
        }
        start();
      }

      refresh();
      host.setAttribute('data-rt-ready', '');

      // Adding or removing an item in the editor changes the cycle underneath us.
      if (window.Shopify && Shopify.designMode) {
        new MutationObserver(refresh).observe(wrap, { childList: true });
      }
    }
  };

  /* ================================================================== */
  /* Tabs                                                                */
  /* ================================================================== */

  function Tabs(root) {
    this.root = root;
    this.cfg = parseJSON(attr(root, 'data-tabs'), {}) || {};
    this.tablist = root.querySelector('[data-tabs-tablist]');
    this.panelsWrap = root.querySelector('[data-tabs-panels]');
    if (!this.tablist || !this.panelsWrap) return;

    this.active = -1;
    this.bind();
    // Tab labels live on the panels (a parent block cannot read a child block's
    // settings), and filling them needs no layout — do it before first paint
    // rather than behind a rAF that a background tab would never run.
    this.syncHeaders();
    // The server already marked the Starting tab active; adopt it without
    // telling a sync group, so sets in one group can open on different tabs.
    this.select(parseInt(this.cfg.start, 10) || 0, { focus: false, animate: false, fromSync: true });

    var self = this;
    requestAnimationFrame(function () {
      if (self.cfg.animate) root.classList.add('tabs--animate-ready');
      self.measure();
      self.warm(self.active);
    });

    // Indicator geometry cannot be measured while the page is hidden; re-measure
    // when it becomes visible so the underline/pill is not left at 0.
    this.onVisible = function () {
      if (!document.hidden) {
        self.syncHeaders();
        self.measure();
      }
    };
    document.addEventListener('visibilitychange', this.onVisible);

    // Media in a tab that is offscreen at load stays cold until the block
    // scrolls into view — then the active panel's images/videos are warmed.
    var visObs = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self.warm(self.active);
            visObs.disconnect();
          }
        }
      },
      { threshold: 0.01 }
    );
    visObs.observe(root);

    this.onResize = function () {
      self.measure();
    };
    window.addEventListener('resize', this.onResize);
  }

  Tabs.prototype.tabs = function () {
    return this.tablist.querySelectorAll('[role="tab"]');
  };

  Tabs.prototype.panels = function () {
    return this.panelsWrap.querySelectorAll('.tabs__panel');
  };

  Tabs.prototype.ref = function (name) {
    return this.root.querySelector('[data-tabs-' + name + ']');
  };

  Tabs.prototype.bind = function () {
    var self = this;

    this.tablist.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (!tab || !self.tablist.contains(tab)) return;
      var tabs = self.tabs();
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i] === tab) {
          self.select(i);
          return;
        }
      }
    });

    this.tablist.addEventListener('keydown', function (e) {
      var count = self.tabs().length;
      if (!count) return;
      var next = self.active;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (self.active - 1 + count) % count;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          next = (self.active + 1) % count;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = count - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      self.select(next, { focus: true });
    });

    if (this.cfg.scroll) {
      // The progress bar is an overlay for freehand scrolling only: it turns
      // on when a wheel or touch drove the scroll, stays on through momentum,
      // and fades out shortly after scrolling stops. Programmatic glides
      // (arrow clicks, selecting a tab) never show it — the arrows are their
      // own feedback.
      var markUserScroll = function () {
        self.userScrolling = true;
        clearTimeout(self.userScrollTimer);
        self.userScrollTimer = setTimeout(function () {
          self.userScrolling = false;
        }, 400);
      };
      this.tablist.addEventListener('wheel', markUserScroll, { passive: true });
      this.tablist.addEventListener('touchmove', markUserScroll, { passive: true });

      this.tablist.addEventListener(
        'scroll',
        function () {
          self.updateScrollUi();
          var bar = self.ref('scrollbar');
          if (!bar || self.tablist.scrollWidth - self.tablist.clientWidth <= 2) return;
          if (self.userScrolling || bar.classList.contains('is-on')) {
            bar.classList.add('is-on');
            clearTimeout(self.barTimer);
            self.barTimer = setTimeout(function () {
              bar.classList.remove('is-on');
            }, 800);
          }
        },
        { passive: true }
      );

      var start = this.ref('arrow-start');
      var end = this.ref('arrow-end');
      if (start) start.addEventListener('click', function () { self.scrollBy(-1); });
      if (end) end.addEventListener('click', function () { self.scrollBy(1); });
    }
  };

  // The tab column runs vertically when a side-position block is on desktop;
  // orientation is read from the computed layout so the mobile fallback (which
  // flips the column back to a row) is picked up automatically.
  Tabs.prototype.isVertical = function () {
    return getComputedStyle(this.tablist).flexDirection.indexOf('column') === 0;
  };

  // Tab button labels live on the panels (each Tab child block owns its header
  // text), so the header row is filled in from the panels rather than duplicated.
  Tabs.prototype.syncHeaders = function () {
    var tabs = this.tabs();
    var panels = this.panels();
    for (var i = 0; i < panels.length; i++) {
      var tab = tabs[i];
      if (!tab) continue;
      var span = tab.querySelector('.s-tabs__tab-text');
      if (!span) continue;
      span.innerHTML = panels[i].dataset.tabHeader || 'Tab';
      span.dataset.text = span.textContent;
      // Each Tab child that sets an icon ships it pre-rendered in a
      // <template> on its panel; it is cloned into the button once.
      var tpl = panels[i].querySelector(':scope > template[data-tab-icon]');
      if (tpl && !tab.querySelector('.s-tabs__tab-icon')) {
        tab.insertBefore(tpl.content.cloneNode(true), span);
      }
    }
  };

  Tabs.prototype.select = function (index, opts) {
    opts = opts || {};
    var tabs = this.tabs();
    var panels = this.panels();
    if (!tabs.length) return;
    index = Math.max(0, Math.min(index, tabs.length - 1));

    var changed = this.active !== index;
    this.active = index;

    for (var i = 0; i < tabs.length; i++) {
      var on = i === index;
      tabs[i].classList.toggle('is-active', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].tabIndex = on ? 0 : -1;
    }

    for (var j = 0; j < panels.length; j++) {
      var active = j === index;
      panels[j].classList.toggle('is-active', active);
      panels[j].setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active && changed && opts.animate !== false && this.cfg.animate) this.enter(panels[j]);
    }

    this.root.classList.add('has-active-tab');
    if (opts.focus) tabs[index].focus();

    var self = this;
    requestAnimationFrame(function () {
      self.measure();
      self.warm(index);
      if (self.cfg.scroll) self.scrollIntoView(index, opts.animate !== false);
    });

    if (changed) emit(this.root, 'sugar:tabs:change', { index: index });
    if (changed && !opts.fromSync && this.cfg.sync) this.broadcast(index);
  };

  // Tab sets that share a Sync group switch together, matched by tab position.
  // A set with fewer tabs than the index leaves its own tab as it is.
  Tabs.prototype.broadcast = function (index) {
    var group = this.cfg.sync;
    var all = document.querySelectorAll('[data-tabs]');
    for (var i = 0; i < all.length; i++) {
      var other = all[i]._sugarTabs;
      if (!other || other === this || other.cfg.sync !== group) continue;
      if (index >= other.tabs().length) continue;
      other.select(index, { focus: false, fromSync: true });
    }
  };

  // Mirrors the enter-transition classes the old markup declared, so the
  // fade-in CSS keeps working untouched.
  Tabs.prototype.enter = function (panel) {
    panel.classList.add('tab-content-enter', 'tab-content-enter-start');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        panel.classList.remove('tab-content-enter-start');
        panel.classList.add('tab-content-enter-end');
        setTimeout(function () {
          panel.classList.remove('tab-content-enter', 'tab-content-enter-end');
        }, 600);
      });
    });
  };

  Tabs.prototype.measure = function () {
    if (this.cfg.underline || this.cfg.hideActiveDivider) this.updateIndicators();
    if (this.cfg.slidingBg) this.updateSlidingBg();
    if (this.cfg.scroll) this.updateScrollUi();
  };

  Tabs.prototype.updateIndicators = function () {
    var header = this.tablist;
    var tab = this.tabs()[this.active];
    if (!tab || !header) return;
    var vertical = this.isVertical();

    if (this.cfg.underline) {
      if (vertical) {
        // Side rail: the CSS positions it on the edge facing the content
        header.style.setProperty('--u-vy', tab.offsetTop + 'px');
        header.style.setProperty('--u-vh', tab.offsetHeight + 'px');
      } else {
        var left = tab.offsetLeft;
        var width = tab.offsetWidth;
        if (this.cfg.underlineText) {
          var span = tab.querySelector('.s-tabs__tab-text');
          if (span) {
            var tabRect = tab.getBoundingClientRect();
            var textRect = span.getBoundingClientRect();
            left += textRect.left - tabRect.left;
            width = textRect.width;
          }
        }
        header.style.setProperty('--u-x', left + 'px');
        header.style.setProperty('--u-w', width + 'px');
        header.style.setProperty('--u-y', tab.offsetTop + tab.offsetHeight + 'px');
      }
    }

    if (this.cfg.hideActiveDivider) {
      var divider = this.ref('divider');
      if (divider) {
        var dividerRect = divider.getBoundingClientRect();
        var rect = tab.getBoundingClientRect();
        if (vertical) {
          divider.style.setProperty('--divider-gap-top', rect.top - dividerRect.top + 'px');
          divider.style.setProperty('--divider-gap-height', rect.height + 'px');
        } else {
          divider.style.setProperty('--divider-gap-left', rect.left - dividerRect.left + 'px');
          divider.style.setProperty('--divider-gap-width', rect.width + 'px');
        }
      }
    }
  };

  Tabs.prototype.updateSlidingBg = function () {
    var bg = this.ref('sliding-bg');
    var tab = this.tabs()[this.active];
    if (!bg || !tab) return;
    bg.style.setProperty('--sliding-bg-top', tab.offsetTop + 'px');
    bg.style.setProperty('--sliding-bg-left', tab.offsetLeft + 'px');
    bg.style.setProperty('--sliding-bg-width', tab.offsetWidth + 'px');
    bg.style.setProperty('--sliding-bg-height', tab.offsetHeight + 'px');
  };

  Tabs.prototype.updateScrollUi = function () {
    var el = this.tablist;
    if (!el) return;
    var overflow = el.scrollWidth - el.clientWidth;
    var canScroll = overflow > 2;
    var canLeft = canScroll && el.scrollLeft > 2;
    var canRight = canScroll && overflow - el.scrollLeft > 2;

    var fadeStart = this.ref('fade-start');
    var fadeEnd = this.ref('fade-end');
    if (fadeStart) fadeStart.style.opacity = canLeft ? '1' : '0';
    if (fadeEnd) fadeEnd.style.opacity = canRight ? '1' : '0';

    // Arrows sit beside the row: both appear whenever the row overflows, and
    // a side that cannot scroll further goes dim instead of disappearing, so
    // the row never jumps while scrolling.
    var arrowStart = this.ref('arrow-start');
    var arrowEnd = this.ref('arrow-end');
    if (arrowStart) {
      arrowStart.classList.toggle('is-on', canScroll);
      arrowStart.classList.toggle('is-off', !canLeft);
    }
    if (arrowEnd) {
      arrowEnd.classList.toggle('is-on', canScroll);
      arrowEnd.classList.toggle('is-off', !canRight);
    }

    var track = this.ref('scrollbar');
    var thumb = this.ref('scrollbar-thumb');
    if (track && thumb) {
      if (!canScroll) {
        track.classList.remove('is-on');
      } else {
        var trackW = track.offsetWidth;
        var thumbW = Math.max(trackW * (el.clientWidth / el.scrollWidth), 20);
        thumb.style.width = thumbW + 'px';
        thumb.style.transform = 'translateX(' + (el.scrollLeft / overflow) * (trackW - thumbW) + 'px)';
      }
    }
  };

  Tabs.prototype.scrollBy = function (direction) {
    this.tablist.scrollBy({ left: direction * this.tablist.clientWidth * 0.6, behavior: 'smooth' });
  };

  // Centres the active tab inside an overflowing strip. Only the strip moves:
  // Element.scrollIntoView would also scroll the window to reach a strip that
  // sits below the fold, which jumped the page on load.
  Tabs.prototype.scrollIntoView = function (index, smooth) {
    var el = this.tablist;
    var tab = this.tabs()[index];
    if (!el || !tab) return;
    var overflow = el.scrollWidth - el.clientWidth;
    if (overflow > 2) {
      var listRect = el.getBoundingClientRect();
      var tabRect = tab.getBoundingClientRect();
      var left = el.scrollLeft + (tabRect.left - listRect.left) - (el.clientWidth - tabRect.width) / 2;
      left = Math.max(0, Math.min(overflow, left));
      if (Math.abs(left - el.scrollLeft) > 1) {
        el.scrollTo({ left: left, behavior: smooth && !instant() ? 'smooth' : 'auto' });
      }
    }
    this.updateScrollUi();
  };

  // Lazy images in the panel about to be shown are promoted to eager and
  // preloaded; autoplaying video plays only in the active panel.
  Tabs.prototype.warm = function (index) {
    var panels = this.panels();
    var panel = panels[index];
    if (!panel) return;

    var viewport = window.innerHeight || document.documentElement.clientHeight;
    panel.querySelectorAll('img[loading="lazy" i]').forEach(function (img) {
      var rect = img.getBoundingClientRect();
      if (rect.bottom < -viewport * 0.25 || rect.top > viewport * 1.5) return;

      img.loading = 'eager';
      if (img.complete) return;

      var srcset = img.getAttribute('srcset');
      var src = img.getAttribute('src');
      if (!srcset && !src) return;

      var preload = new Image();
      if (srcset) {
        preload.sizes = img.getAttribute('sizes') || '';
        preload.srcset = srcset;
      }
      if (src) preload.src = src;
    });

    panels.forEach(function (p, i) {
      p.querySelectorAll('video[autoplay]').forEach(function (v) {
        if (i === index) {
          if (v._lazyRestore) v._lazyRestore();
          if (v.paused) v.play().catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    });
  };

  Tabs.init = function (scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var found = [];
    if (root.matches && root.matches('[data-tabs]')) found.push(root);
    var els = root.querySelectorAll('[data-tabs]');
    for (var i = 0; i < els.length; i++) found.push(els[i]);

    for (var j = 0; j < found.length; j++) {
      if (found[j]._sugarTabs) continue;
      found[j]._sugarTabs = new Tabs(found[j]);
    }
  };

  /* ================================================================== */
  /* Quiz                                                                */
  /* ================================================================== */

  /*
   * A quiz funnel is spread across sibling sections (quiz-main owns the chrome,
   * each quiz-page / quiz-loading is one step), so its state cannot live on any
   * one element. `SugarQuiz` is that shared state; sections call `subscribe()`
   * with a render function and the store calls them back after every mutation.
   */
  var Quiz = {
    currentStep: 0,
    totalSteps: 0,
    pages: [],
    answers: {},
    pageRedirects: {},
    pageRequired: {},
    pageHideProgress: {},
    pageHideFromCount: {},
    pageAnswerType: {},
    pageOwnsAdvance: {},
    validationError: null,
    completed: false,
    hasEnd: false,
    hasSalesPage: false,
    colorScheme: '',
    customColors: null,
    showAll: false,
    showGuides: false,
    hideMobileNav: false,
    analyticsId: '',
    template: '',
    nav: {
      nextText: '',
      nextStyle: 'primary',
      nextArrow: false,
      hideNextSingle: false,
      hideEverywhere: false,
      nextCustomBg: '',
      nextCustomText: '',
      fullWidthButtons: false,
      nextVerticalPadding: 14,
      nextHorizontalPadding: 28,
      backLocation: 'before_next',
      showBack: true,
      backHideBorder: false,
      showSkip: false,
      skipStyle: 'text',
      skipEmojiUrl: '',
      skipEmojiPosition: 'left',
      skipText: '',
      skipLink: ''
    },

    /* ---------- subscriptions ---------- */

    _subs: [],

    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      Quiz._subs.push(fn);
      try {
        fn(Quiz);
      } catch (e) {}
      return function () {
        var i = Quiz._subs.indexOf(fn);
        if (i !== -1) Quiz._subs.splice(i, 1);
      };
    },

    notify: function () {
      for (var i = 0; i < Quiz._subs.length; i++) {
        try {
          Quiz._subs[i](Quiz);
        } catch (e) {}
      }
    },

    configure: function (cfg) {
      if (!cfg) return;
      var keys = Object.keys(cfg);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i] === 'nav') {
          var navKeys = Object.keys(cfg.nav || {});
          for (var j = 0; j < navKeys.length; j++) Quiz.nav[navKeys[j]] = cfg.nav[navKeys[j]];
        } else {
          Quiz[keys[i]] = cfg[keys[i]];
        }
      }
      if ('editorPath' in cfg && (cfg.editorPath || (window.Shopify && window.Shopify.designMode))) Quiz.refreshPaths();
      Quiz.notify();
    },

    /* ---------- page registry ---------- */

    pageElement: function (sectionId) {
      return document.getElementById('quiz-page-' + sectionId) || document.getElementById('quiz-loading-' + sectionId);
    },

    pageIdFromElement: function (el) {
      if (!el || !el.id) return null;
      if (el.id.indexOf('quiz-page-') === 0) return el.id.replace('quiz-page-', '');
      if (el.id.indexOf('quiz-loading-') === 0) return el.id.replace('quiz-loading-', '');
      return null;
    },

    syncPagesFromDom: function (currentPageId, leaving) {
      var els = document.querySelectorAll('.quiz-page[id^="quiz-page-"], .quiz-loading[id^="quiz-loading-"]');
      for (var i = 0; i < els.length; i++) {
        var pageId = Quiz.pageIdFromElement(els[i]);
        if (pageId && Quiz.pages.indexOf(pageId) === -1) Quiz.pages.push(pageId);
      }
      var activePageId = currentPageId || Quiz.pages[Quiz.currentStep];
      Quiz.applyPathPages(activePageId, leaving);
      Quiz.sortPages(activePageId);
    },

    // Steps are ordered by document position, not registration order — the
    // editor can add, move or re-render a step section at any time.
    sortPages: function (currentPageId) {
      var activePageId = currentPageId || Quiz.pages[Quiz.currentStep];
      Quiz.pages.sort(function (a, b) {
        var aEl = Quiz.pageElement(a);
        var bEl = Quiz.pageElement(b);
        if (!aEl || !bEl || aEl === bEl) return 0;
        var position = aEl.compareDocumentPosition(bEl);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      Quiz.totalSteps = Quiz.pages.length;

      if (activePageId) {
        var idx = Quiz.pages.indexOf(activePageId);
        if (idx !== -1) {
          Quiz.currentStep = idx;
          return;
        }
      }
      if (Quiz.currentStep >= Quiz.totalSteps && Quiz.totalSteps > 0) Quiz.currentStep = Quiz.totalSteps - 1;
    },

    designMode: function () {
      return typeof Shopify !== 'undefined' && Shopify.designMode;
    },

    register: function (sectionId) {
      var saved = Quiz.designMode() ? sessionStorage.getItem('_qes') : null;
      var activePageId = saved || (Quiz.pages.length ? Quiz.pages[Quiz.currentStep] : null);
      if (Quiz.pages.indexOf(sectionId) === -1) Quiz.pages.push(sectionId);
      Quiz.syncPagesFromDom(activePageId);

      if (Quiz.designMode() && saved) {
        var idx = Quiz.pages.indexOf(saved);
        if (idx !== -1) Quiz.currentStep = idx;
      }
      Quiz.notify();
    },

    unregister: function (sectionId) {
      var idx = Quiz.pages.indexOf(sectionId);
      if (idx === -1) return;
      Quiz.pages.splice(idx, 1);
      Quiz.totalSteps = Quiz.pages.length;
      if (Quiz.currentStep >= Quiz.totalSteps && Quiz.totalSteps > 0) Quiz.currentStep = Quiz.totalSteps - 1;
      Quiz.notify();
    },

    setPageRedirect: function (sectionId, url) {
      if (url) Quiz.pageRedirects[sectionId] = url;
      else delete Quiz.pageRedirects[sectionId];
    },

    setPageRequired: function (sectionId, errorText) {
      Quiz.pageRequired[sectionId] = errorText;
    },

    setPageHideProgress: function (sectionId) {
      Quiz.pageHideProgress[sectionId] = true;
    },

    setPageHideFromCount: function (sectionId) {
      Quiz.pageHideFromCount[sectionId] = true;
    },

    /* A step carrying its own advance control (a Quiz Continue block) takes over
       from the shared Next button, so the step does not show two of them. */
    setPageOwnsAdvance: function (sectionId) {
      Quiz.pageOwnsAdvance[sectionId] = true;
      Quiz.notify();
    },

    registerAnswers: function (sectionId, isMulti) {
      Quiz.pageAnswerType[sectionId] = isMulti ? 'multi' : 'single';
      Quiz.notify();
    },

    /* ---------- paths ---------- */

    /*
     * A Quiz Content with "Show for answer tag" filled in (data-quiz-path) is one path's part of
     * a step: it shows once an earlier pick carries one of those Customer tags. A step left with
     * nothing to show, and nothing a later pick could still show, is taken out of `pages`, so
     * Next/Back, progress, pagination and analytics all count the shopper's own path. The editor
     * shows every path and skips nothing.
     */
    pathSkipped: {},
    pathDropped: {},

    pathsChanged: function () {
      if (Quiz.designMode() || !document.querySelector('[data-quiz-path]')) return;
      Quiz.syncPagesFromDom(Quiz.pages[Quiz.currentStep]);
      Quiz.prefetchNext();
    },

    applyPathPages: function (activePageId, leaving) {
      var back = Object.keys(Quiz.pathDropped);
      for (var i = 0; i < back.length; i++) if (Quiz.pages.indexOf(back[i]) === -1) Quiz.pages.push(back[i]);
      Quiz.pathDropped = {};
      Quiz.refreshPaths(activePageId, leaving);
      for (var j = Quiz.pages.length - 1; j >= 0; j--) {
        var id = Quiz.pages[j];
        if (Quiz.pathSkipped[id] && id !== activePageId) {
          Quiz.pathDropped[id] = true;
          Quiz.pages.splice(j, 1);
        }
      }
    },

    /* Theme editor: Quiz Main's "Show only path" narrows the canvas to one path (or a comma list)
       plus the shared content. A step with nothing on it says so instead of showing blank. */
    editorPath: '',

    applyEditorPath: function () {
      var want = String(Quiz.editorPath || '')
        .toLowerCase()
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
      var writes = [];
      document.querySelectorAll('[data-quiz-page], [data-quiz-loading]').forEach(function (step) {
        var inner = step.querySelector('.quiz-page-inner');
        if (!inner) return;
        var contents = step.querySelectorAll('.sugar-quiz-content');
        var shown = 0;
        contents.forEach(function (el) {
          var tags = el.hasAttribute('data-quiz-path') ? el.dataset.quizPath.split(',') : null;
          var on = !want.length || !tags || tags.some(function (t) { return want.indexOf(t) !== -1; });
          if (on) shown++;
          writes.push([el, on]);
        });
        var note = inner.querySelector(':scope > [data-quiz-path-note]');
        writes.push([inner, note, want.length && contents.length && !shown ? want.join(', ') : '']);
      });
      writes.forEach(function (w) {
        if (w.length === 2) {
          if (w[0].hidden === w[1]) w[0].hidden = !w[1];
          return;
        }
        var inner = w[0], note = w[1], label = w[2];
        if (!label) {
          if (note) note.remove();
          return;
        }
        if (!note) {
          note = document.createElement('p');
          note.setAttribute('data-quiz-path-note', '');
          note.style.cssText =
            'margin:0;padding:16px;text-align:center;font-size:14px;line-height:1.5;border:1px dashed rgba(var(--color-foreground),0.35);border-radius:8px;color:rgba(var(--color-foreground),0.6)';
          inner.insertBefore(note, inner.firstChild);
        }
        var text = 'Not on path: ' + label + '. Clear Show only path to see every path.';
        if (note.textContent !== text) note.textContent = text;
      });
    },

    refreshPaths: function (activePageId, leaving) {
      Quiz.pathSkipped = {};
      if (Quiz.designMode()) return Quiz.applyEditorPath();
      if (!document.querySelector('[data-quiz-path]')) return;

      function tagsOf(raw) {
        return String(raw || '')
          .toLowerCase()
          .split(',')
          .map(function (t) { return t.trim(); })
          .filter(Boolean);
      }
      function stepOf(el) {
        var page = el.closest('[data-quiz-page], [data-quiz-loading]');
        return page ? page.dataset.quizPage || page.dataset.quizLoading : null;
      }
      function hit(tags, set) {
        for (var i = 0; i < tags.length; i++) if (set[tags[i]]) return true;
        return false;
      }

      /* Read everything first: the steps in page order, each step's shared and path content,
         and which step asks each tag. Attribute reads only, no layout. */
      var order = [], pos = {}, stepEl = {}, info = {}, tagAsks = {};
      document.querySelectorAll('[data-quiz-page], [data-quiz-loading]').forEach(function (el) {
        var id = el.dataset.quizPage || el.dataset.quizLoading;
        if (!id || id in pos) return;
        pos[id] = order.length;
        order.push(id);
        stepEl[id] = el;
        info[id] = { paths: [], shared: false };
      });
      document.querySelectorAll('.sugar-quiz-content').forEach(function (el) {
        var id = stepOf(el);
        if (!id || !info[id]) return;
        if (el.hasAttribute('data-quiz-path')) info[id].paths.push({ el: el, tags: tagsOf(el.dataset.quizPath), on: false });
        else info[id].shared = true;
      });
      document.querySelectorAll('[data-answer-tag]').forEach(function (el) {
        var id = stepOf(el);
        if (!id || !info[id]) return;
        tagsOf(el.dataset.answerTag).forEach(function (t) {
          (tagAsks[t] = tagAsks[t] || []).push({ step: id, el: el });
        });
      });

      var cur = activePageId in pos ? pos[activePageId] : -1;
      var picked = {}, drop = {}, flags = {};

      /* A step is decided once it holds a pick that stays (picks inside a path that just closed
         don't), or the shopper is past it, or is leaving it now. */
      function decided(id) {
        var p = pos[id];
        var kept = (Quiz.answers[id] || []).filter(function (a) { return !drop[id] || drop[id].indexOf(a) === -1; });
        return kept.length > 0 || p < cur || (!!leaving && p === cur);
      }
      function pathOf(step, el) {
        var path = el.closest('[data-quiz-path]');
        if (!path) return null;
        for (var i = 0; i < step.paths.length; i++) if (step.paths[i].el === path) return step.paths[i];
        return null;
      }
      function pathOn(step, el) {
        var p = pathOf(step, el);
        return !p || p.on;
      }
      /* A tag can still be picked at step `id` if an answer carrying it sits on this step or an
         earlier one that is still in the run and undecided, and that answer can itself show. */
      function tagPossible(t, id) {
        return (tagAsks[t] || []).some(function (a) {
          if (pos[a.step] > pos[id] || Quiz.pathSkipped[a.step] || decided(a.step)) return false;
          var p = pathOf(info[a.step], a.el);
          return !p || p.on || !!p.possible;
        });
      }

      order.forEach(function (id) {
        var step = info[id];
        var root = stepEl[id];
        var inPath = [];
        /* Picks outside any path count first, so a path can follow a pick on its own step. */
        (Quiz.answers[id] || []).forEach(function (a) {
          var el = root.querySelector('[data-answer-id=' + JSON.stringify(String(a)) + ']');
          if (!el) return;
          if (el.closest('[data-quiz-path]')) inPath.push([a, el]);
          else tagsOf(el.dataset.answerTag).forEach(function (t) { picked[t] = true; });
        });
        if (!step.paths.length) return;

        var anyOn = false;
        step.paths.forEach(function (p) {
          p.on = hit(p.tags, picked);
          p.possible = false;
          if (p.on) anyOn = true;
        });
        inPath.forEach(function (pair) {
          if (pathOn(step, pair[1])) tagsOf(pair[1].dataset.answerTag).forEach(function (t) { picked[t] = true; });
          else (drop[id] = drop[id] || []).push(pair[0]);
        });

        var anyPossible = false;
        step.paths.forEach(function (p) {
          if (p.on) return;
          p.possible = p.tags.some(function (t) { return tagPossible(t, id); });
          if (p.possible) anyPossible = true;
        });
        if (!step.shared && !anyOn && !anyPossible) Quiz.pathSkipped[id] = true;

        /* What the step asks of the shopper comes from the content that is showing. */
        var f = { type: null, required: null, owns: false };
        root.querySelectorAll('[data-quiz-answers-root]').forEach(function (el) {
          if (!pathOn(step, el)) return;
          var multi = el.dataset.multi === 'true' && !el.hasAttribute('data-quiz-scale');
          f.type = f.type === 'multi' || multi ? 'multi' : 'single';
          if (!f.required && el.dataset.requiredMessage) f.required = el.dataset.requiredMessage;
        });
        root.querySelectorAll('[data-quiz-email-required]').forEach(function (el) {
          if (!f.required && pathOn(step, el)) f.required = el.dataset.quizEmailRequired;
        });
        root.querySelectorAll('.sugar-quiz-continue').forEach(function (el) {
          if (pathOn(step, el)) f.owns = true;
        });
        flags[id] = f;
      });

      /* Then write. */
      order.forEach(function (id) {
        info[id].paths.forEach(function (p) {
          if (p.el.hidden === p.on) p.el.hidden = !p.on;
        });
      });
      Object.keys(drop).forEach(function (id) {
        var keep = (Quiz.answers[id] || []).filter(function (a) { return drop[id].indexOf(a) === -1; });
        if (keep.length) Quiz.answers[id] = keep;
        else delete Quiz.answers[id];
      });
      Object.keys(flags).forEach(function (id) {
        var f = flags[id];
        if (f.type) Quiz.pageAnswerType[id] = f.type;
        else delete Quiz.pageAnswerType[id];
        if (f.required) Quiz.pageRequired[id] = f.required;
        else delete Quiz.pageRequired[id];
        if (f.owns) Quiz.pageOwnsAdvance[id] = true;
        else delete Quiz.pageOwnsAdvance[id];
      });
    },

    /* ---------- counting ---------- */

    visibleTotal: function () {
      var count = 0;
      for (var i = 0; i < Quiz.pages.length; i++) if (!Quiz.pageHideFromCount[Quiz.pages[i]]) count++;
      return count;
    },

    visibleStep: function () {
      var count = 0;
      for (var i = 0; i <= Quiz.currentStep && i < Quiz.pages.length; i++) {
        if (!Quiz.pageHideFromCount[Quiz.pages[i]]) count++;
      }
      return Math.max(0, count - 1);
    },

    hasEndPage: function () {
      return Quiz.hasEnd;
    },

    isActive: function (sectionId) {
      if (Quiz.completed) return false;
      return Quiz.pages[Quiz.currentStep] === sectionId;
    },

    progress: function () {
      if (Quiz.totalSteps <= 1) return 0;
      return Math.round((Quiz.currentStep / (Quiz.totalSteps - 1)) * 100);
    },

    /* ---------- analytics ---------- */

    emitQuizEvent: function (type, value, extra) {
      if (Quiz.designMode()) return;
      var pageTitle = (document.title || '').split(/\s+[|–—]\s+/)[0].trim().slice(0, 80);
      var ctx = Object.assign({ template: Quiz.template, path: window.location.pathname, label: pageTitle }, extra || {});
      if (window.SugarAnalytics) SugarAnalytics.track(type, 'quiz', Quiz.analyticsId, value, ctx);
      else (window._sugarQ = window._sugarQ || []).push([type, 'quiz', Quiz.analyticsId, value, ctx]);
    },

    quizSessionOnce: function (key) {
      if (window.SugarAnalytics) return SugarAnalytics.sessionOnce(key);
      try {
        if (sessionStorage.getItem(key)) return false;
        sessionStorage.setItem(key, '1');
        return true;
      } catch (e) {
        return true;
      }
    },

    /* ---------- navigation ---------- */

    complete: function () {
      Quiz.completed = true;
      document.body.classList.add('quiz-completed');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (Quiz.quizSessionOnce('_sugar_conv_complete_' + Quiz.analyticsId)) Quiz.emitQuizEvent('convert', 'complete');
      Quiz.notify();
    },

    next: function () {
      Quiz.syncPagesFromDom(Quiz.pages[Quiz.currentStep], true);
      /* The re-count above can change the total (quiz paths), so even a refused move re-renders. */
      if (Quiz.currentStep >= Quiz.totalSteps) return Quiz.notify();

      var currentPageId = Quiz.pages[Quiz.currentStep];
      var isLast = Quiz.currentStep === Quiz.totalSteps - 1;
      var redirectUrl = Quiz.pageRedirects[currentPageId];
      if (isLast && !Quiz.hasEndPage() && !Quiz.hasSalesPage && !redirectUrl) return Quiz.notify();

      var reqError = Quiz.pageRequired[currentPageId];
      if (reqError && !(Quiz.answers[currentPageId] && Quiz.answers[currentPageId].length > 0)) {
        Quiz.validationError = currentPageId;
        Quiz.notify();
        return;
      }
      Quiz.validationError = null;

      if (redirectUrl && !Quiz.designMode()) {
        if (Quiz.quizSessionOnce('_sugar_conv_complete_' + Quiz.analyticsId)) {
          Quiz.emitQuizEvent('convert', 'complete');
          if (window.SugarAnalytics) window.SugarAnalytics.flush();
        }
        window.location.href = redirectUrl;
        return;
      }

      if (isLast) {
        Quiz.complete();
      } else {
        Quiz.currentStep++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        Quiz.emitQuizEvent('step', String(Quiz.currentStep + 1), { section: Quiz.pages[Quiz.currentStep] || '' });
      }
      if (Quiz.designMode()) sessionStorage.setItem('_qes', Quiz.pages[Quiz.currentStep]);
      Quiz.prefetchNext();
      Quiz.notify();
    },

    prev: function () {
      Quiz.syncPagesFromDom(Quiz.pages[Quiz.currentStep]);
      if (Quiz.currentStep <= 0) return;
      Quiz.currentStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (Quiz.designMode()) sessionStorage.setItem('_qes', Quiz.pages[Quiz.currentStep]);
      Quiz.prefetchNext();
      Quiz.notify();
    },

    /* ---------- answers ---------- */

    selectAnswer: function (step, answerId, isMulti) {
      if (isMulti) {
        var arr = (Quiz.answers[step] || []).slice();
        var ni = arr.indexOf('__none__');
        if (ni !== -1) arr.splice(ni, 1);
        var idx = arr.indexOf(answerId);
        idx === -1 ? arr.push(answerId) : arr.splice(idx, 1);
        Quiz.answers[step] = arr;
      } else {
        Quiz.answers[step] = [answerId];
        if (Quiz.nav && (Quiz.nav.hideNextSingle || Quiz.nav.hideEverywhere)) {
          setTimeout(function () {
            if (Quiz.isActive(step)) Quiz.next();
          }, 300);
        }
      }

      var scope = Quiz.pageElement(step);
      var answerEl = scope && scope.querySelector('[data-answer-id=' + JSON.stringify(answerId) + ']');
      var textEl = answerEl && (answerEl.querySelector('.quiz-answer__text') || answerEl);
      var label = textEl && textEl.textContent ? textEl.textContent.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      Quiz.emitQuizEvent('select', step + ':' + answerId, { label: label, section: step });

      if (Quiz.validationError === step) Quiz.validationError = null;
      Quiz.pathsChanged();
      Quiz.notify();
    },

    setAnswers: function (step, values) {
      if (values && values.length) Quiz.answers[step] = values;
      else delete Quiz.answers[step];
      Quiz.pathsChanged();
      Quiz.notify();
    },

    isSelected: function (step, answerId) {
      if (!Quiz.answers[step]) return false;
      return Quiz.answers[step].indexOf(answerId) !== -1;
    },

    /* ---------- prefetch ---------- */

    // The next two steps are already in the DOM but their images have never
    // been asked for; warming them keeps the step transition from flashing.
    prefetchNext: function () {
      for (var o = 1; o <= 2; o++) {
        var ni = Quiz.currentStep + o;
        if (ni >= Quiz.pages.length) break;
        var pel = document.getElementById('quiz-page-' + Quiz.pages[ni]);
        if (!pel) continue;
        /* Per picture, and never inside a path the shopper isn't on (see paths). */
        pel.querySelectorAll('img').forEach(function (img) {
          if (img._pf || img.closest('[data-quiz-path][hidden]')) return;
          img._pf = true;
          var srcset = img.getAttribute('srcset');
          var src = img.getAttribute('src');
          if (srcset) {
            var l = document.createElement('link');
            l.rel = 'preload';
            l.as = 'image';
            l.setAttribute('imagesrcset', srcset);
            l.setAttribute('imagesizes', img.getAttribute('sizes') || '');
            document.head.appendChild(l);
          } else if (src && src.indexOf('data:') !== 0) {
            var p = new Image();
            p.src = src;
          }
        });
      }
    }
  };

  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('[data-quiz-next], [data-quiz-back]');
    if (!el) return;
    e.preventDefault();
    el.hasAttribute('data-quiz-back') ? Quiz.prev() : Quiz.next();
  });

  /* ================================================================== */
  /* Jump nav                                                            */
  /* ================================================================== */

  // The Mini Link Buttons row: pills that glide to an anchor on the page and
  // light up while that anchor is the one being read.
  var JumpNav = {
    rows: [],

    init: function (scope) {
      var els = (scope || document).querySelectorAll('[data-mlb]');
      for (var i = 0; i < els.length; i++) JumpNav.mount(els[i]);
      JumpNav.watch();
      JumpNav.sync();
    },

    mount: function (root) {
      if (root._mlb) return;
      root._mlb = true;
      JumpNav.rows.push(root);

      var strip = root.querySelector('[data-mlb-row]');
      var bar = root.querySelector('[data-mlb-bar]');
      if (!strip || !bar) return;

      // The bar is feedback for freehand scrolling only: it turns on when a
      // wheel or a finger drove the row and fades out once the row settles.
      var driven = false;
      var drivenTimer;
      var barTimer;
      function markDriven() {
        driven = true;
        clearTimeout(drivenTimer);
        drivenTimer = setTimeout(function () { driven = false; }, 400);
      }
      strip.addEventListener('wheel', markDriven, { passive: true });
      strip.addEventListener('touchmove', markDriven, { passive: true });

      strip.addEventListener(
        'scroll',
        function () {
          var overflow = strip.scrollWidth - strip.clientWidth;
          if (overflow <= 2) {
            bar.classList.remove('is-on');
            return;
          }
          var thumb = bar.querySelector('[data-mlb-bar-thumb]');
          if (thumb) {
            var trackW = bar.offsetWidth;
            var thumbW = Math.max(trackW * (strip.clientWidth / strip.scrollWidth), 20);
            thumb.style.width = thumbW + 'px';
            thumb.style.transform = 'translateX(' + (strip.scrollLeft / overflow) * (trackW - thumbW) + 'px)';
          }
          if (driven || bar.classList.contains('is-on')) {
            bar.classList.add('is-on');
            clearTimeout(barTimer);
            barTimer = setTimeout(function () { bar.classList.remove('is-on'); }, 800);
          }
        },
        { passive: true }
      );
    },

    glide: function (root, target) {
      var header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      var offset = parseInt(root.getAttribute('data-mlb-offset'), 10) || 0;

      // Otherwise a sticky header slides in on the upward leg of the scroll and
      // covers the very thing the pill was pointing at.
      var sticky = document.querySelector('sticky-header');
      if (sticky) sticky.dispatchEvent(new Event('preventHeaderReveal'));

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - header - offset,
        behavior: instant() ? 'auto' : 'smooth'
      });
    },

    // The active pill is the last anchor to have crossed the reading line, found
    // by geometry rather than by whichever observer entry happened to fire last
    // — with two anchors on screen, callback order would flicker between them.
    sync: function () {
      var line = (window.innerHeight || document.documentElement.clientHeight || 0) * 0.3;

      for (var r = JumpNav.rows.length - 1; r >= 0; r--) {
        var root = JumpNav.rows[r];
        if (!root.isConnected) {
          JumpNav.rows.splice(r, 1);
          continue;
        }

        var pills = root.querySelectorAll('[data-mlb-target]');
        var best = null;
        var bestTop = -Infinity;
        for (var i = 0; i < pills.length; i++) {
          var target = document.getElementById(pills[i].getAttribute('data-mlb-target'));
          if (!target) continue;
          var top = target.getBoundingClientRect().top;
          if (top <= line && top > bestTop) {
            bestTop = top;
            best = pills[i];
          }
        }
        for (var j = 0; j < pills.length; j++) {
          var on = pills[j] === best;
          pills[j].classList.toggle('is-active', on);
          on ? pills[j].setAttribute('aria-current', 'true') : pills[j].removeAttribute('aria-current');
        }
      }
    },

    watch: function () {
      if (JumpNav.watching) return;
      JumpNav.watching = true;
      // rAF is paused in a hidden tab, so the pending frame is tracked by id and
      // replaced rather than gated behind a flag — a flag set during a scroll the
      // tab never painted would never clear, and the highlight would stay dead.
      var frame = 0;
      function schedule() {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(function () {
          frame = 0;
          JumpNav.sync();
        });
      }
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) schedule();
      });
    }
  };

  document.addEventListener('click', function (e) {
    var pill = e.target.closest && e.target.closest('[data-mlb-target]');
    if (!pill) return;
    var root = pill.closest('[data-mlb]');
    if (!root) return;
    var target = document.getElementById(pill.getAttribute('data-mlb-target'));
    if (!target) return;
    e.preventDefault();
    JumpNav.glide(root, target);
  });

  /* ================================================================== */
  /* Boot                                                                */
  /* ================================================================== */

  function init(scope) {
    stamp(scope);
    Accordion.init(scope);
    Clamp.init(scope);
    Rotator.init(scope);
    Tabs.init(scope);
    JumpNav.init(scope);
  }

  // Custom-element sugar for markup that can afford its own wrapper tag: the
  // element just stamps the group attributes the engines above look for.
  function defineGroupElement(name, attrs) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(
      name,
      class extends HTMLElement {
        connectedCallback() {
          for (var i = 0; i < attrs.length; i++) {
            if (!this.hasAttribute(attrs[i])) this.setAttribute(attrs[i], '');
          }
          init(this);
        }
      }
    );
  }

  defineGroupElement('sugar-accordion', ['data-accordion-group']);
  defineGroupElement('sugar-tabs', ['data-tabs']);

  window.SugarInteractive = {
    accordion: Accordion,
    clamp: Clamp,
    rotator: Rotator,
    tabs: Tabs,
    jumpNav: JumpNav,
    stamp: stamp,
    init: init
  };
  window.SugarQuiz = Quiz;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  // Theme editor section/block re-renders and the Section Rendering API drop in
  // fresh markup that never runs our boot path.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) { init(e.target); });
})();
